import { Link } from "@inertiajs/react";
import { useRef, useState } from "react";
import { AlertTriangle, ShieldAlert } from "lucide-react";
import AppLayout from "../../layouts/AppLayout";
import { usePipelineUpdates } from "../../hooks/usePipelineUpdates";

const STAGE_LABELS: Record<string, string> = {
  new_lead: "New Lead",
  intake_scheduled: "Intake Scheduled",
  resume_editing: "Resume Editing",
  package_pending: "Package Pending",
  submitted: "Submitted",
  skill_marketed: "Skill Marketed",
  screener: "Screener",
  full_round: "Full Round",
  offer_placed: "Offer / Placed",
};

// Shown as subtitle under each column header — explains stage purpose and any automations.
const STAGE_HINTS: Record<string, string> = {
  new_lead:           "Warm leads only — must pass intake gate",
  intake_scheduled:   "AI resume optimizer runs when enabled",
  resume_editing:     "Refine resume before packaging",
  package_pending:    "Auto-generate submission package when enabled",
  submitted:          "Actively submitted to firm — matched against job alerts",
  skill_marketed:     "Skill-marketed to firms without an open role",
  screener:           "Screener interview stage",
  full_round:         "In-firm full-round interviews",
  offer_placed:       "Offer extended or placement confirmed",
};

const PRIORITY_COLORS: Record<string, string> = {
  high:   "bg-red-100 text-red-700",
  normal: "bg-gray-100 text-gray-600",
  low:    "bg-blue-50 text-blue-600",
};

interface Candidate {
  id: number;
  full_name: string;
  firm_name: string;
  practice_area: string;
  pipeline_stage: string;
  priority: string;
  dnc_status: string;
  days_in_stage: number;
  stale: boolean;
}

interface Column {
  stage: string;
  candidates: Candidate[];
  count: number;
}

interface Props {
  columns: Column[];
}

export default function PipelineIndex({ columns: initial }: Props) {
  const [columns, setColumns] = useState(initial);
  const dragRef = useRef<{ candidateId: number; fromStage: string } | null>(null);
  const [dragOverStage, setDragOverStage] = useState<string | null>(null);
  const [moveError, setMoveError] = useState<string | null>(null);

  const total = columns.reduce((sum, col) => sum + col.count, 0);

  // Real-time: another user moved a card
  usePipelineUpdates({
    onStageMoved: ({ candidate_id, from_stage, to_stage }) => {
      setColumns((prev) => {
        const candidate = prev.flatMap((c) => c.candidates).find((c) => c.id === candidate_id);
        if (!candidate) return prev;
        return prev.map((col) => {
          if (col.stage === from_stage) {
            return { ...col, candidates: col.candidates.filter((c) => c.id !== candidate_id), count: col.count - 1 };
          }
          if (col.stage === to_stage) {
            return { ...col, candidates: [...col.candidates, { ...candidate, pipeline_stage: to_stage }], count: col.count + 1 };
          }
          return col;
        });
      });
    },
  });

  function onDragStart(candidateId: number, fromStage: string) {
    dragRef.current = { candidateId, fromStage };
  }

  function onDragOver(e: React.DragEvent, stage: string) {
    e.preventDefault();
    setDragOverStage(stage);
  }

  function onDragLeave() {
    setDragOverStage(null);
  }

  async function onDrop(e: React.DragEvent, toStage: string) {
    e.preventDefault();
    setDragOverStage(null);
    const drag = dragRef.current;
    if (!drag || drag.fromStage === toStage) return;

    // Optimistic update
    const candidate = columns.flatMap((c) => c.candidates).find((c) => c.id === drag.candidateId);
    if (!candidate) return;

    setColumns((prev) =>
      prev.map((col) => {
        if (col.stage === drag.fromStage) {
          return { ...col, candidates: col.candidates.filter((c) => c.id !== drag.candidateId), count: col.count - 1 };
        }
        if (col.stage === toStage) {
          return { ...col, candidates: [...col.candidates, { ...candidate, pipeline_stage: toStage }], count: col.count + 1 };
        }
        return col;
      })
    );

    try {
      const res = await fetch("/pipeline/move", {
        method: "PATCH",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": csrfToken() },
        body: JSON.stringify({ candidate_id: drag.candidateId, to_stage: toStage }),
      });
      if (!res.ok) {
        const body = await res.json();
        setMoveError(body.error ?? "Move failed.");
        // Revert optimistic update
        setColumns((prev) =>
          prev.map((col) => {
            if (col.stage === toStage) {
              return { ...col, candidates: col.candidates.filter((c) => c.id !== drag.candidateId), count: col.count - 1 };
            }
            if (col.stage === drag.fromStage) {
              return { ...col, candidates: [...col.candidates, candidate], count: col.count + 1 };
            }
            return col;
          })
        );
        setTimeout(() => setMoveError(null), 4000);
      }
    } catch {
      setMoveError("Network error — move not saved.");
      setTimeout(() => setMoveError(null), 4000);
    }

    dragRef.current = null;
  }

  return (
    <AppLayout title="Pipeline">
      {moveError && (
        <div className="mb-3 flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-2 rounded-lg">
          <ShieldAlert className="w-4 h-4 flex-shrink-0" />
          {moveError}
        </div>
      )}

      <div className="mb-4 flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-gray-800">Pipeline</h1>
          <p className="text-sm text-gray-500 mt-0.5">{total} candidate{total !== 1 ? "s" : ""} in pipeline</p>
        </div>
        <Link
          href="/candidates/new"
          className="no-underline text-sm bg-green-700 text-white px-3 py-1.5 rounded-md hover:bg-green-800 transition-colors"
        >
          + Add Candidate
        </Link>
      </div>

      <div className="flex gap-3 overflow-x-auto pb-4 [&::-webkit-scrollbar]:h-1.5 [&::-webkit-scrollbar-track]:bg-gray-100 [&::-webkit-scrollbar-track]:rounded-full [&::-webkit-scrollbar-thumb]:bg-gray-300 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb:hover]:bg-gray-400">
        {columns.map(({ stage, candidates, count }) => (
          <div
            key={stage}
            className="flex-shrink-0 w-56"
            onDragOver={(e) => onDragOver(e, stage)}
            onDragLeave={onDragLeave}
            onDrop={(e) => onDrop(e, stage)}
          >
            {/* Column header */}
            <div className={`flex items-center justify-between mb-2 px-1 py-1 rounded transition-colors ${dragOverStage === stage ? "bg-green-50" : ""}`}>
              <div>
                <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
                  {STAGE_LABELS[stage] ?? stage}
                </span>
                {STAGE_HINTS[stage] && (
                  <p className="text-xs text-gray-400 font-normal normal-case tracking-normal">{STAGE_HINTS[stage]}</p>
                )}
              </div>
              <span className="text-xs text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded-full">
                {count}
              </span>
            </div>

            {/* Cards */}
            <div className={`space-y-2 min-h-16 rounded-lg transition-colors ${dragOverStage === stage ? "bg-green-50/50 ring-1 ring-green-200" : ""}`}>
              {candidates.map((c) => {
                const dnc_blocked = c.dnc_status !== "clear";

                return (
                  <div
                    key={c.id}
                    draggable
                    onDragStart={() => onDragStart(c.id, stage)}
                    className={`bg-white border rounded-lg p-3 transition-all select-none cursor-grab hover:border-green-400 hover:shadow-sm border-gray-200 ${c.stale ? "border-l-2 border-l-amber-400" : ""} ${c.priority === "high" ? "border-t-2 border-t-red-400" : ""}`}
                  >
                    <div className="flex items-start justify-between gap-1">
                      <Link
                        href={`/candidates/${c.id}`}
                        className="text-sm font-medium text-gray-900 truncate hover:text-green-700 flex-1"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {c.full_name}
                      </Link>
                    </div>

                    <p className="text-xs text-gray-500 truncate mt-0.5">{c.firm_name}</p>
                    {c.practice_area && (
                      <p className="text-xs text-gray-400 truncate">{c.practice_area}</p>
                    )}

                    <div className="flex items-center justify-between mt-2">
                      <span className={`text-xs px-1.5 py-0.5 rounded ${PRIORITY_COLORS[c.priority] ?? "bg-gray-100 text-gray-600"}`}>
                        {c.priority}
                      </span>
                      <div className="flex items-center gap-1">
                        {c.stale && <AlertTriangle className="w-3 h-3 text-amber-500" title="Needs attention — no movement in 7+ days" />}
                        {c.days_in_stage > 0 && (
                          <span className={`text-xs ${c.stale ? "text-amber-600 font-medium" : "text-gray-400"}`}>
                            {c.days_in_stage}d
                          </span>
                        )}
                      </div>
                    </div>

                    {dnc_blocked && (
                      <div className="flex items-center gap-1 mt-1.5" title="Do Not Contact — active DNC rule blocks outreach to this candidate's firm">
                        <ShieldAlert className="w-3 h-3 text-red-500" />
                        <span className="text-xs text-red-600">DNC blocked</span>
                      </div>
                    )}
                  </div>
                );
              })}

              {candidates.length === 0 && (
                <div className={`border border-dashed rounded-lg h-16 transition-colors ${dragOverStage === stage ? "border-green-300 bg-green-50" : "border-gray-200"}`} />
              )}
            </div>
          </div>
        ))}
      </div>
    </AppLayout>
  );
}

function csrfToken(): string {
  return (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement)?.content ?? "";
}
